import { useId, type ReactNode } from "react";
import { cx } from "./cx";
import { Icon } from "./icons";
import s from "./metric.module.css";

export interface DeltaPillProps {
  tone: "good" | "bad" | "flat";
  direction?: "up" | "down";
  children: ReactNode;
}

export function DeltaPill({ tone, direction, children }: DeltaPillProps) {
  return (
    <span className={cx(s.pill, s[tone])} data-tone={tone}>
      {direction ? <Icon name={direction === "up" ? "up" : "dn"} /> : null}
      {children}
    </span>
  );
}

export type SparkTone = "neutral" | "good" | "bad";

/** Hand-written SVG area + line, as in the reference. No chart library. */
export function Sparkline({ data, tone = "neutral" }: { data: readonly number[]; tone?: SparkTone }) {
  const gid = useId();
  const w = 200;
  const h = 42;
  const pts = data.length > 1 ? data : [data[0] ?? 0, data[0] ?? 0];
  const mn = Math.min(...pts);
  const mx = Math.max(...pts);
  const rg = mx - mn || 1;
  const xy = pts.map((v, i) => [(i * w) / (pts.length - 1), h - 4 - ((v - mn) / rg) * (h - 14)] as const);
  const line = "M" + xy.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join("L");
  return (
    <svg className={cx(s.spark, s[`s-${tone}`])} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" data-tone={tone}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity=".18" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line}L${w} ${h}L0 ${h}Z`} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export interface MetricTileProps {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  delta?: DeltaPillProps;
  spark?: readonly number[];
  sparkTone?: SparkTone;
}

/** One cell of the metrics strip. Put tiles inside `MetricStrip`. */
export function MetricTile({ label, value, note, delta, spark, sparkTone }: MetricTileProps) {
  return (
    <div className={s.met}>
      <div className={s.head}>
        <span className={s.label}>{label}</span>
        {delta ? <DeltaPill {...delta} /> : null}
      </div>
      <b className={s.value}>{value}</b>
      {note ? <span className={s.note}>{note}</span> : null}
      {spark && spark.length > 0 ? <Sparkline data={spark} tone={sparkTone} /> : <div className={s.gap} />}
    </div>
  );
}

export function MetricStrip({ children, label }: { children: ReactNode; label: string }) {
  return <section className={cx(s.mets)} aria-label={label} data-metric-strip="">{children}</section>;
}
